import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import { ReactComponent as Logo } from '../assets/PM_Logo.svg';
import { ReactComponent as MenuIcon } from '../assets/menu.svg';
import { ReactComponent as CloseIcon } from '../assets/x.svg';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { to: '/blog', label: 'Blog' },
  { to: '/photos', label: 'Grid' },
];

// Light / dark switch. Shown to everyone, admin included: which palettes light
// and dark map to is a site-wide setting edited in the admin console
// (admin.parthmerchant.com → Appearance), but flipping between them is a
// per-visitor choice and the admin is a visitor too.
function ThemeMenu() {
  const { toggle, isDark } = useTheme();

  // A single icon that flips — it shows where you're going, not where you are.
  const Icon = isDark ? LightModeRoundedIcon : DarkModeRoundedIcon;
  return (
    <button
      className="theme-flip-btn"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      <Icon sx={{ fontSize: 18 }} />
    </button>
  );
}

// Authenticated account control: avatar button → popover with email, dashboard,
// and sign out. Matches the ThemeMenu popover pattern (custom, theme-aware).
function UserMenu({ email, picture, onSignOut }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const ref = useRef(null);
  const initial = (email || 'A').charAt(0).toUpperCase();
  const showPhoto = picture && !imgError;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        className={`user-avatar ${open ? 'active' : ''} ${showPhoto ? 'has-photo' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Account menu"
        aria-haspopup="true"
        aria-expanded={open}
        title={email || 'Account'}
      >
        {showPhoto
          ? <img src={picture} alt="" className="user-avatar-img" onError={() => setImgError(true)} referrerPolicy="no-referrer" />
          : initial}
      </button>

      {open && (
        <div className="user-popover" role="menu">
          <div className="user-popover-account">
            <span className="user-popover-label">Signed in as</span>
            <span className="user-popover-email">{email || 'Admin'}</span>
          </div>
          <button
            className="user-popover-item"
            role="menuitem"
            onClick={() => { setOpen(false); onSignOut(); navigate('/'); }}
          >
            <LogoutRoundedIcon sx={{ fontSize: 17 }} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthed, isAdmin, email, picture, signOut } = useAuth();
  const visibleNavItems = navItems;

  if (isAdmin) {
    return (
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="header-logo" aria-label="Home">
            <Logo className="logo-svg" />
          </Link>
          <nav className="header-nav desktop-nav">
            <UserMenu email={email} picture={picture} onSignOut={signOut} />
            <ThemeMenu />
          </nav>
          <div className="mobile-controls">
            <UserMenu email={email} picture={picture} onSignOut={signOut} />
            <ThemeMenu />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="header">
      <div className="header-inner">
        <Link to="/" className="header-logo" aria-label="Home" onClick={() => setMenuOpen(false)}>
          <Logo className="logo-svg" />
        </Link>

        {/* Desktop nav */}
        <nav className="header-nav desktop-nav">
          {visibleNavItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthed && (
            <NavLink
              to="/admin/dashboard"
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              Space Native
            </NavLink>
          )}
          {isAuthed && (
            <UserMenu email={email} picture={picture} onSignOut={signOut} />
          )}
          <ThemeMenu />
        </nav>

        {/* Mobile controls */}
        <div className="mobile-controls">
          {isAuthed && <UserMenu email={email} picture={picture} onSignOut={signOut} />}
          <ThemeMenu />
          <button
            className="menu-btn"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {menuOpen
              ? <CloseIcon className="icon-svg" />
              : <MenuIcon className="icon-svg" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <nav className="mobile-nav">
          {visibleNavItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthed && (
            <NavLink
              to="/admin/dashboard"
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              Space Native
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
}
